import React from 'react';
import { useLearningAnalytics } from '../hooks/useAnalytics';
import { AnalyticsFilters } from '../types/analytics.types';
import { KPICard } from './KPICard';
import { CompletionRatesChart } from './CompletionRatesChart';
import { SuccessRatesChart } from './SuccessRatesChart';
import { VelocityMetrics } from './VelocityMetrics';
import { ComponentPerformance } from './ComponentPerformance';
import './LearningTab.css';

interface LearningTabProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
}

export const LearningTab: React.FC<LearningTabProps> = ({ filters, onFiltersChange }) => {
  const { data, loading, error, refetch } = useLearningAnalytics(filters);

  if (error) {
    return (
      <div className="learning-tab">
        <div className="error-message">
          <h3>❌ Error Loading Learning Data</h3>
          <p>{error.message}</p>
          <button onClick={() => refetch()} className="retry-button">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="learning-tab">
      {/* Summary KPIs */}
      <div className="learning-summary">
        <h2>Learning Outcomes Overview</h2>
        <div className="kpi-grid">
          {loading ? (
            <>
              <KPICard title="Overall Success" value="0%" loading={true} />
              <KPICard title="Completion Rate" value="0%" loading={true} />
              <KPICard title="Total Learners" value="0" loading={true} />
              <KPICard title="Learning Actions" value="0" loading={true} />
            </>
          ) : data?.summary ? (
            <>
              <KPICard
                title="Overall Success"
                value={`${data.summary.overall_success_rate.toFixed(1)}%`}
                subtitle="Correct answers"
                icon="✅"
              />
              <KPICard
                title="Completion Rate"
                value={`${data.summary.completion_rate.toFixed(1)}%`}
                subtitle={`${data.summary.sections_completed} sections`}
                icon="🎯"
              />
              <KPICard
                title="Total Learners"
                value={data.summary.total_unique_learners.toLocaleString()}
                subtitle="Active users"
                icon="👥"
              />
              <KPICard
                title="Learning Actions"
                value={data.summary.total_learning_actions.toLocaleString()}
                subtitle={`${data.summary.avg_actions_per_user.toFixed(0)} per user`}
                icon="🚀"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Success Rates Analysis */}
      {data?.success && (
        <div className="learning-section">
          <SuccessRatesChart data={data.success} loading={loading} />
        </div>
      )}

      {/* Completion Rates */}
      {data?.completion && (
        <div className="learning-section">
          <CompletionRatesChart data={data.completion} loading={loading} />
        </div>
      )}

      {/* Component Performance */}
      {data?.summary && (
        <div className="learning-section">
          <ComponentPerformance data={data.summary} loading={loading} />
        </div>
      )}

      {/* Velocity Metrics */}
      {data?.velocity && (
        <div className="learning-section">
          <VelocityMetrics data={data.velocity} loading={loading} />
        </div>
      )}

      {/* Additional Insights */}
      {data?.summary && !loading && (
        <div className="learning-insights">
          <h3>💡 Key Learning Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <span className="insight-icon">📈</span>
              <div className="insight-content">
                <h4>Learning Activity</h4>
                <p>
                  {data.summary.total_unique_learners.toLocaleString()} learners completed{' '}
                  {data.summary.total_learning_actions.toLocaleString()} learning actions
                  {data.summary.avg_actions_per_user > 0 && (
                    <>, averaging {data.summary.avg_actions_per_user.toFixed(0)} actions per user</>
                  )}
                </p>
              </div>
            </div>

            {data.success && (
              <div className="insight-card">
                <span className="insight-icon">✨</span>
                <div className="insight-content">
                  <h4>Success Performance</h4>
                  <p>
                    Overall success rate of {data.success.overall_success_rate.toFixed(1)}%
                    {data.success.avg_response_time_ms > 0 && (
                      <> with an average response time of {(data.success.avg_response_time_ms / 1000).toFixed(1)} seconds</>
                    )}
                  </p>
                </div>
              </div>
            )}

            {data.completion && (
              <div className="insight-card">
                <span className="insight-icon">🎯</span>
                <div className="insight-content">
                  <h4>Completion Progress</h4>
                  <p>
                    {data.completion.overall_completion_rate.toFixed(1)}% completion rate
                    {data.completion.avg_time_to_completion_days > 0 && (
                      <>, with sections taking an average of {data.completion.avg_time_to_completion_days.toFixed(1)} days to complete</>
                    )}
                  </p>
                </div>
              </div>
            )}

            {data.velocity && data.velocity.avg_velocity_sections_per_week > 0 && (
              <div className="insight-card">
                <span className="insight-icon">⚡</span>
                <div className="insight-content">
                  <h4>Learning Velocity</h4>
                  <p>
                    Learners are completing an average of{' '}
                    {data.velocity.avg_velocity_sections_per_week.toFixed(2)} sections per week,
                    based on {data.velocity.total_users_analyzed.toLocaleString()} active users
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningTab;
